import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../config';
import ConfigurationGuides from '../ConfigurationGuides';
import AIAccessConsent from './AIAccessConsent';
import LauncherEditor from './LauncherEditor';
import { useConfirm } from '../ConfirmProvider';

function SettingsTab({ fetchWithAuth, pushNotifications, token, setActiveTab }) {
  const confirm = useConfirm();
  const [settingsSubTab, setSettingsSubTab] = useState('notifications');

  // Hydration Settings State
  const [hydrationSettings, setHydrationSettings] = useState({
    push_reminders_enabled: false,
    daily_goal_ml: 1893,
    reminder_times: ['08:00', '14:00', '19:00'],
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles'
  });
  const [hydrationLoading, setHydrationLoading] = useState(false);
  const [hydrationSaved, setHydrationSaved] = useState(false);
  const [hydrationError, setHydrationError] = useState('');

  // Meal Reminder Settings State
  const [mealSettings, setMealSettings] = useState({
    meal_reminders: false,
    meal_reminder_times: { breakfast: '08:00', lunch: '12:00', dinner: '18:00', snack: '15:00' },
    daily_calorie_goal: null,
    daily_protein_goal: null,
    daily_carbs_goal: null,
    daily_fat_goal: null
  });
  const [mealSettingsLoading, setMealSettingsLoading] = useState(false);
  const [recommendedMacros, setRecommendedMacros] = useState({ calories: 2000, protein: 150, carbs: 200, fat: 65 });

  // AI Assistant Personalization State
  const [botIcon, setBotIcon] = useState('🧠');
  const [botName, setBotName] = useState('AI Assistant');
  const [botAvailableIcons, setBotAvailableIcons] = useState([]);
  const [botAvailableNames, setBotAvailableNames] = useState([]);
  const [botSaving, setBotSaving] = useState(false);
  const [botSaved, setBotSaved] = useState(false);
  const [botError, setBotError] = useState('');

  // Notification Preferences State
  const [notifPrefs, setNotifPrefs] = useState({
    hydration_reminders: true,
    meal_plan_alerts: true,
    medication_reminders: true,
    goal_achievements: true,
    device_sync_alerts: false,
    health_anomaly_alerts: false,
    provider_messages: true,
    weekly_summary: true,
    pantry_expiration: true,
    quiet_hours_enabled: false,
    quiet_hours_start: '22:00',
    quiet_hours_end: '07:00',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles'
  });
  const [notifPrefsLoading, setNotifPrefsLoading] = useState(false);
  const [testLoading, setTestLoading] = useState('');
  const [quietHoursSaved, setQuietHoursSaved] = useState(false);

  // Load settings on mount
  useEffect(() => {
    loadHydrationSettings();
    loadMealSettings();
    loadNotifPrefs();
    loadBotPersonalization();
  }, []);

  const loadHydrationSettings = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/dashboard/settings/hydration-reminders`);
      if (response.ok) {
        const data = await response.json();
        setHydrationSettings({
          push_reminders_enabled: data.push_reminders_enabled || false,
          daily_goal_ml: data.daily_goal_ml || 1893,
          reminder_times: data.reminder_times || ['08:00', '14:00', '19:00'],
          timezone: data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
        });
      }
    } catch (error) {
      console.error('Failed to load hydration settings:', error);
    }
  };

  const saveHydrationSettings = async () => {
    setHydrationLoading(true);
    setHydrationError('');
    setHydrationSaved(false);

    try {
      const response = await fetchWithAuth(`${API_BASE}/api/dashboard/settings/hydration-reminders`, {
        method: 'POST',
        body: JSON.stringify(hydrationSettings)
      });

      if (response.ok) {
        setHydrationSaved(true);
        setTimeout(() => setHydrationSaved(false), 3000);
      } else {
        const data = await response.json();
        setHydrationError(data.detail || 'Failed to save settings');
      }
    } catch (error) {
      setHydrationError('Failed to save settings. Please try again.');
    } finally {
      setHydrationLoading(false);
    }
  };

  const loadMealSettings = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/dashboard/settings/meal-reminders`);
      if (response.ok) {
        const data = await response.json();
        setMealSettings({
          meal_reminders: data.meal_reminders || false,
          meal_reminder_times: data.meal_reminder_times || { breakfast: '08:00', lunch: '12:00', dinner: '18:00', snack: '15:00' },
          daily_calorie_goal: data.daily_calorie_goal,
          daily_protein_goal: data.daily_protein_goal,
          daily_carbs_goal: data.daily_carbs_goal,
          daily_fat_goal: data.daily_fat_goal
        });
        setRecommendedMacros(data.recommended || { calories: 2000, protein: 150, carbs: 200, fat: 65 });
      }
    } catch (error) {
      console.error('Failed to load meal settings:', error);
    }
  };

  const saveMealSettings = async () => {
    setMealSettingsLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/dashboard/settings/meal-reminders`, {
        method: 'POST',
        body: JSON.stringify(mealSettings)
      });
      if (response.ok) {
        alert('Meal reminder settings saved!');
      }
    } catch (error) {
      console.error('Failed to save meal settings:', error);
    } finally {
      setMealSettingsLoading(false);
    }
  };

  // Notification Preferences load/save
  const loadNotifPrefs = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/push/preferences`);
      if (response.ok) {
        const data = await response.json();
        setNotifPrefs({
          hydration_reminders: data.hydration_reminders ?? true,
          meal_plan_alerts: data.meal_plan_alerts ?? true,
          medication_reminders: data.medication_reminders ?? true,
          goal_achievements: data.goal_achievements ?? true,
          device_sync_alerts: data.device_sync_alerts ?? false,
          health_anomaly_alerts: data.health_anomaly_alerts ?? false,
          provider_messages: data.provider_messages ?? true,
          weekly_summary: data.weekly_summary ?? true,
          pantry_expiration: data.pantry_expiration ?? true,
          quiet_hours_enabled: data.quiet_hours_enabled ?? false,
          quiet_hours_start: data.quiet_hours_start || '22:00',
          quiet_hours_end: data.quiet_hours_end || '07:00',
          timezone: data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles'
        });
      }
    } catch (error) {
      console.error('Failed to load notification preferences:', error);
    }
  };

  const loadBotPersonalization = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/support/personalize`);
      if (response.ok) {
        const data = await response.json();
        setBotIcon(data.icon || '🧠');
        setBotName(data.name || 'AI Assistant');
        setBotAvailableIcons(data.available_icons || []);
        setBotAvailableNames(data.available_names || []);
      }
    } catch (error) {
      console.error('Failed to load bot personalization:', error);
    }
  };

  const saveBotPersonalization = async () => {
    setBotSaving(true);
    setBotError('');
    setBotSaved(false);
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/support/personalize`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ icon: botIcon, name: botName }),
      });
      if (response.ok) {
        setBotSaved(true);
        setTimeout(() => setBotSaved(false), 3000);
      } else {
        const data = await response.json();
        setBotError(data.detail || 'Failed to save');
      }
    } catch (error) {
      setBotError('Failed to save personalization');
    }
    setBotSaving(false);
  };

  const saveNotifPref = async (field, value) => {
    try {
      await fetchWithAuth(`${API_BASE}/api/push/preferences`, {
        method: 'POST',
        body: JSON.stringify({ [field]: value })
      });
    } catch (error) {
      console.error('Failed to save notification preference:', error);
    }
  };

  const toggleNotifType = async (field) => {
    const newVal = !notifPrefs[field];
    setNotifPrefs(prev => ({ ...prev, [field]: newVal }));
    await saveNotifPref(field, newVal);
  };

  const saveQuietHours = async () => {
    setNotifPrefsLoading(true);
    try {
      await fetchWithAuth(`${API_BASE}/api/push/preferences`, {
        method: 'POST',
        body: JSON.stringify({
          quiet_hours_enabled: notifPrefs.quiet_hours_enabled,
          quiet_hours_start: notifPrefs.quiet_hours_start,
          quiet_hours_end: notifPrefs.quiet_hours_end
        })
      });
      setQuietHoursSaved(true);
      setTimeout(() => setQuietHoursSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save quiet hours:', error);
    } finally {
      setNotifPrefsLoading(false);
    }
  };

  const saveTimezone = async (tz) => {
    setNotifPrefs(prev => ({ ...prev, timezone: tz }));
    await saveNotifPref('timezone', tz);
    // Also update hydration settings timezone to keep in sync
    setHydrationSettings(prev => ({ ...prev, timezone: tz }));
  };

  const sendTypeTest = async (type) => {
    setTestLoading(type);
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/push/test?type=${type}`, {
        method: 'POST'
      });
      const data = await response.json();
      if (!data.success) {
        alert(data.detail || data.message || 'Failed to send test notification');
      }
    } catch (error) {
      alert('Failed to send test notification: ' + error.message);
    } finally {
      setTestLoading('');
    }
  };

  const NOTIF_TYPES = [
    { key: 'hydration_reminders', label: 'Hydration Reminders', icon: '\u{1F4A7}', testType: 'hydration', desc: 'Reminders to drink water throughout the day', active: true },
    { key: 'meal_plan_alerts', label: 'Meal Alerts', icon: '\u{1F37D}\u{FE0F}', testType: 'meal', desc: 'Notifications when it\'s time for scheduled meals', active: true },
    { key: 'medication_reminders', label: 'Medication Reminders', icon: '\u{1F48A}', testType: 'medication', desc: 'Reminders to take your medications on time', active: true },
    { key: 'goal_achievements', label: 'Goal Achievements', icon: '\u{1F389}', testType: 'goal', desc: 'Celebrate when you reach your health goals', active: true },
    { key: 'weekly_summary', label: 'Weekly Summary', icon: '\u{1F4CA}', testType: 'weekly', desc: 'Weekly health report with trends and insights', active: true },
    { key: 'pantry_expiration', label: 'Pantry Expiration', icon: '\u{26A0}\u{FE0F}', testType: 'pantry', desc: 'Alerts when pantry items are about to expire', active: true },
    { key: 'provider_messages', label: 'Provider Messages', icon: '\u{1F468}\u{200D}\u{2695}\u{FE0F}', testType: 'provider', desc: 'Notifications when your provider responds', active: true },
    { key: 'device_sync_alerts', label: 'Device Sync Alerts', icon: '\u{1F4F1}', testType: 'device_sync', desc: 'Alerts when your connected device stops syncing', active: true },
    { key: 'health_anomaly_alerts', label: 'Health Alerts', icon: '\u{1F4C8}', testType: 'health_anomaly', desc: 'Lets you know by push and email when a reading is well outside your own usual range. A reading far outside it always appears in your notifications, even with this off. Compares with your own history; not a diagnosis, not real-time monitoring, not for emergencies.', active: true }
  ];

  const updateReminderTime = (index, newTime) => {
    const newTimes = [...hydrationSettings.reminder_times];
    newTimes[index] = newTime;
    setHydrationSettings(prev => ({ ...prev, reminder_times: newTimes }));
  };

  const addReminderTime = () => {
    if (hydrationSettings.reminder_times.length < 6) {
      setHydrationSettings(prev => ({
        ...prev,
        reminder_times: [...prev.reminder_times, '12:00']
      }));
    }
  };

  const removeReminderTime = (index) => {
    if (hydrationSettings.reminder_times.length > 1) {
      const newTimes = hydrationSettings.reminder_times.filter((_, i) => i !== index);
      setHydrationSettings(prev => ({ ...prev, reminder_times: newTimes }));
    }
  };

  // Convert ml to oz for display
  const mlToOz = (ml) => Math.round(ml / 29.574);

  return (
    <div className="page-layout">
      <div className="page-sticky-nav">
        <div className="page-header-row">
          <h2 className="page-section-title">Application Settings</h2>
          <button className="page-exit-btn" onClick={() => setActiveTab('dashboard')}>← Dashboard</button>
        </div>

        <div className="page-nav-bar">
          {/* Tab Bar */}
          <div className="tab-bar">
            {[
              { id: 'notifications', label: 'Notifications' },
              { id: 'hydration', label: 'Hydration Reminders' },
              { id: 'meals', label: 'Meal Reminders' },
              { id: 'ai-assistant', label: 'AI Assistant' },
              { id: 'launcher', label: 'Dashboard Launcher' },
              { id: 'ai-access', label: 'Connected Data Access' },
              { id: 'guides', label: 'Configuration Guides' }
            ].map(tab => (
              <button
                key={tab.id}
                className={`tab-item ${settingsSubTab === tab.id ? 'active' : ''}`}
                onClick={() => setSettingsSubTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="page-scroll-content">
      {/* Notifications Tab */}
      {settingsSubTab === 'notifications' && (
        <div className="ns-container">
          {/* Push Subscription Management */}
          <div className="card ns-card">
            <div className="card-header">
              <h3 className="card-title">Push Notification Status</h3>
            </div>
            <div className="ns-cardBody">
              <div className={`push-status-box ${pushNotifications?.isSubscribed ? 'push-status-box--active' : 'push-status-box--inactive'}`}>
                <div className="push-status-header">
                  <span className={`push-status-indicator ${pushNotifications?.isSubscribed ? 'push-status-indicator--active' : 'push-status-indicator--inactive'}`}></span>
                  <strong className={`push-status-title ${pushNotifications?.isSubscribed ? 'push-status-title--active' : 'push-status-title--inactive'}`}>
                    {pushNotifications?.isSubscribed ? 'Push notifications are active' : 'Push notifications are not enabled'}
                  </strong>
                </div>

                {pushNotifications && !pushNotifications.isSupported && (
                  <p className="push-status-message push-status-message--error">
                    Your browser does not support push notifications. Please use Chrome, Edge, or Firefox.
                  </p>
                )}

                {pushNotifications?.isSupported && pushNotifications.permission === 'denied' && (
                  <p className="push-status-message push-status-message--error">
                    Notification permissions are blocked. Enable notifications in browser settings and refresh.
                  </p>
                )}

                {pushNotifications?.isSupported && !pushNotifications.isSubscribed && pushNotifications.permission !== 'denied' && (
                  <div>
                    <p className="push-status-message push-status-message--info">
                      Enable push notifications to receive health reminders on this device.
                    </p>
                    <button
                      className="push-enable-btn"
                      onClick={async () => {
                        try {
                          await pushNotifications.subscribe();
                        } catch (err) {
                          console.error('Push subscribe failed:', err);
                          alert('Failed to enable push notifications: ' + err.message);
                        }
                      }}
                      disabled={pushNotifications.loading}
                    >
                      {pushNotifications.loading ? 'Enabling...' : 'Enable Push Notifications'}
                    </button>
                  </div>
                )}

                {pushNotifications?.isSubscribed && (
                  <div>
                    <p className="push-status-timezone">
                      Notifications sent based on your timezone ({notifPrefs.timezone}).
                    </p>
                    <button
                      className="push-disable-btn"
                      onClick={async () => {
                        if (await confirm({
                          title: 'Disable notifications on this device?',
                          message: 'You will stop receiving hydration, meal and health reminders here. You can turn them back on any time from this screen.',
                          confirmLabel: 'Disable',
                          destructive: true,
                        })) {
                          try {
                            await pushNotifications.unsubscribe();
                          } catch (err) {
                            console.error('Unsubscribe failed:', err);
                          }
                        }
                      }}
                    >
                      Disable Notifications
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Timezone */}
          <div className="card ns-card">
            <div className="card-header">
              <h3 className="card-title">Timezone</h3>
            </div>
            <div className="ns-cardBody">
              <p className="ns-typeDesc">All notification times are based on your selected timezone.</p>
              <select
                className="hydration-timezone-select"
                value={notifPrefs.timezone}
                onChange={(e) => saveTimezone(e.target.value)}
              >
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="America/Anchorage">Alaska Time (AKT)</option>
                <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
              </select>
            </div>
          </div>

          {/* Quiet Hours */}
          <div className="card ns-card">
            <div className="card-header">
              <h3 className="card-title">Quiet Hours</h3>
            </div>
            <div className="ns-cardBody">
              <label className="hydration-toggle-label">
                <input
                  type="checkbox"
                  className="hydration-checkbox"
                  checked={notifPrefs.quiet_hours_enabled}
                  onChange={(e) => setNotifPrefs(prev => ({ ...prev, quiet_hours_enabled: e.target.checked }))}
                />
                <span className="hydration-toggle-text">Enable Quiet Hours</span>
              </label>
              <p className="ns-typeDesc">No notifications will be sent during quiet hours.</p>

              {notifPrefs.quiet_hours_enabled && (
                <div className="ns-quietRow">
                  <div className="ns-quietField">
                    <label className="ns-quietLabel">Start</label>
                    <input
                      type="time"
                      className="hydration-time-input"
                      value={notifPrefs.quiet_hours_start}
                      onChange={(e) => setNotifPrefs(prev => ({ ...prev, quiet_hours_start: e.target.value }))}
                    />
                  </div>
                  <div className="ns-quietField">
                    <label className="ns-quietLabel">End</label>
                    <input
                      type="time"
                      className="hydration-time-input"
                      value={notifPrefs.quiet_hours_end}
                      onChange={(e) => setNotifPrefs(prev => ({ ...prev, quiet_hours_end: e.target.value }))}
                    />
                  </div>
                </div>
              )}

              {quietHoursSaved && (
                <div className="hydration-alert-success ns-quietSaved">
                  &#10003; Quiet hours saved!
                </div>
              )}
              <button
                className="ns-saveBtn"
                onClick={saveQuietHours}
                disabled={notifPrefsLoading}
              >
                {notifPrefsLoading ? 'Saving...' : 'Save Quiet Hours'}
              </button>
            </div>
          </div>

          {/* Notification Types */}
          <div className="card ns-card">
            <div className="card-header">
              <h3 className="card-title">Notification Types</h3>
            </div>
            <div className="ns-cardBody">
              <div className="ns-typeGrid">
                {NOTIF_TYPES.map(nt => (
                  <div key={nt.key} className="ns-typeRow">
                    <span className="ns-typeIcon">{nt.icon}</span>
                    <div className="ns-typeInfo">
                      <div className="ns-typeName">
                        {nt.label}
                        <span className={`ns-typeBadge ${nt.active ? 'ns-typeBadge--active' : 'ns-typeBadge--soon'}`}>
                          {nt.active ? 'Active' : 'Coming Soon'}
                        </span>
                      </div>
                      <div className="ns-typeDesc">{nt.desc}</div>
                    </div>
                    <div className="ns-typeActions">
                      <label className="ns-toggleSwitch">
                        <input
                          type="checkbox"
                          checked={notifPrefs[nt.key] ?? true}
                          onChange={() => toggleNotifType(nt.key)}
                        />
                        <span className="ns-toggleSlider"></span>
                      </label>
                      {pushNotifications?.isSubscribed && (
                        <button
                          className="ns-typeTestBtn"
                          onClick={() => sendTypeTest(nt.testType)}
                          disabled={testLoading === nt.testType}
                        >
                          {testLoading === nt.testType ? 'Sending...' : 'Send Test'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hydration Reminders Tab */}
      {settingsSubTab === 'hydration' && (
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">💧 Hydration Reminder Settings</h3>
        </div>
        <div className="hydration-settings-content">
          {hydrationError && (
            <div className="hydration-alert-error">
              {hydrationError}
            </div>
          )}

          {hydrationSaved && (
            <div className="hydration-alert-success">
              ✓ Settings saved successfully!
            </div>
          )}

          {/* Enable/Disable Toggle */}
          <div className="hydration-form-section">
            <label className="hydration-toggle-label">
              <input
                type="checkbox"
                className="hydration-checkbox"
                checked={hydrationSettings.push_reminders_enabled}
                onChange={(e) => setHydrationSettings(prev => ({
                  ...prev,
                  push_reminders_enabled: e.target.checked
                }))}
              />
              <span className="hydration-toggle-text">Enable Push Notification Hydration Reminders</span>
            </label>
            <p className="hydration-toggle-description">
              Receive push notification reminders to drink water throughout the day
            </p>
          </div>

          {hydrationSettings.push_reminders_enabled && (
            <>
              {/* Daily Goal */}
              <div className="hydration-form-section">
                <label className="hydration-form-label">Daily Water Goal</label>
                <div className="hydration-goal-row">
                  <input
                    type="range"
                    className="hydration-goal-slider"
                    min="946"
                    max="3785"
                    step="118"
                    value={hydrationSettings.daily_goal_ml}
                    onChange={(e) => setHydrationSettings(prev => ({
                      ...prev,
                      daily_goal_ml: parseInt(e.target.value)
                    }))}
                  />
                  <span className="hydration-goal-display">
                    {mlToOz(hydrationSettings.daily_goal_ml)} oz
                  </span>
                </div>
                <p className="hydration-hint-text">
                  Recommended: 0.5 oz per pound of body weight (64-100 oz for most adults)
                </p>
              </div>

              {/* Reminder Times */}
              <div className="hydration-form-section">
                <label className="hydration-form-label">Reminder Times</label>
                <div className="hydration-times-list">
                  {hydrationSettings.reminder_times.map((time, index) => (
                    <div key={index} className="hydration-time-row">
                      <input
                        type="time"
                        className="hydration-time-input"
                        value={time}
                        onChange={(e) => updateReminderTime(index, e.target.value)}
                      />
                      <span className="hydration-time-label">
                        {index === 0 ? 'Morning' : index === 1 ? 'Afternoon' : index === 2 ? 'Evening' : `Reminder ${index + 1}`}
                      </span>
                      {hydrationSettings.reminder_times.length > 1 && (
                        <button
                          className="hydration-time-remove-btn"
                          onClick={() => removeReminderTime(index)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {hydrationSettings.reminder_times.length < 6 && (
                  <button className="hydration-add-time-btn" onClick={addReminderTime}>
                    + Add Reminder Time
                  </button>
                )}
              </div>

              {/* Timezone & Push Subscription — now in Notifications tab */}
            </>
          )}

          {/* Save Button */}
          <button
            className="hydration-save-btn"
            onClick={saveHydrationSettings}
            disabled={hydrationLoading}
          >
            {hydrationLoading ? 'Saving...' : 'Save Hydration Settings'}
          </button>
        </div>
      </div>
      )}

      {/* Meal Reminders Tab */}
      {settingsSubTab === 'meals' && (
      <div className="card meal-settings-card">
        <div className="card-header">
          <h3 className="card-title">🍽️ Meal Reminder Settings</h3>
        </div>
        <div className="meal-settings-content">

          {/* Enable Toggle */}
          <div className="meal-settings-toggle">
            <label>
              <input
                type="checkbox"
                checked={mealSettings?.meal_reminders || false}
                onChange={(e) => setMealSettings(prev => ({ ...prev, meal_reminders: e.target.checked }))}
              />
              <span className="meal-settings-toggle-label">Enable Push Meal Reminders</span>
            </label>
            <p className="meal-settings-toggle-desc">
              Get notified when it's time for your scheduled meals
            </p>
          </div>

          {mealSettings?.meal_reminders && (
            <>
              {/* Meal Times */}
              <div className="meal-times-section">
                <label>Reminder Times</label>
                <div className="meal-times-grid">
                  {['breakfast', 'lunch', 'dinner', 'snack'].map(mealType => (
                    <div key={mealType} className="meal-time-row">
                      <span className="meal-time-label">
                        {mealType}
                      </span>
                      <input
                        type="time"
                        className="meal-time-input"
                        value={mealSettings?.meal_reminder_times?.[mealType] || '12:00'}
                        onChange={(e) => setMealSettings(prev => ({
                          ...prev,
                          meal_reminder_times: { ...prev.meal_reminder_times, [mealType]: e.target.value }
                        }))}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Calorie Goal */}
              <div className="macro-slider-section">
                <div className="macro-slider-header">
                  <label className="macro-slider-label">Daily Calorie Goal</label>
                  <span className="macro-slider-recommended">Recommended: {recommendedMacros?.calories}</span>
                </div>
                <div className="macro-slider-row">
                  <input type="range" min="1200" max="4000" step="50"
                    value={mealSettings?.daily_calorie_goal || recommendedMacros?.calories || 2000}
                    onChange={(e) => setMealSettings(prev => ({ ...prev, daily_calorie_goal: parseInt(e.target.value) }))}
                  />
                  <span className="macro-slider-value">
                    {mealSettings?.daily_calorie_goal || recommendedMacros?.calories || 2000} cal
                  </span>
                </div>
              </div>

              {/* Protein Goal */}
              <div className="macro-slider-section">
                <div className="macro-slider-header">
                  <label className="macro-slider-label">Daily Protein Goal</label>
                  <span className="macro-slider-recommended">Recommended: {recommendedMacros?.protein}g</span>
                </div>
                <div className="macro-slider-row">
                  <input type="range" min="50" max="300" step="5"
                    value={mealSettings?.daily_protein_goal || recommendedMacros?.protein || 150}
                    onChange={(e) => setMealSettings(prev => ({ ...prev, daily_protein_goal: parseInt(e.target.value) }))}
                  />
                  <span className="macro-slider-value">
                    {mealSettings?.daily_protein_goal || recommendedMacros?.protein || 150}g
                  </span>
                </div>
              </div>

              {/* Carbs Goal */}
              <div className="macro-slider-section">
                <div className="macro-slider-header">
                  <label className="macro-slider-label">Daily Carbs Goal</label>
                  <span className="macro-slider-recommended">Recommended: {recommendedMacros?.carbs}g</span>
                </div>
                <div className="macro-slider-row">
                  <input type="range" min="50" max="400" step="10"
                    value={mealSettings?.daily_carbs_goal || recommendedMacros?.carbs || 200}
                    onChange={(e) => setMealSettings(prev => ({ ...prev, daily_carbs_goal: parseInt(e.target.value) }))}
                  />
                  <span className="macro-slider-value">
                    {mealSettings?.daily_carbs_goal || recommendedMacros?.carbs || 200}g
                  </span>
                </div>
              </div>

              {/* Fat Goal */}
              <div className="macro-slider-section">
                <div className="macro-slider-header">
                  <label className="macro-slider-label">Daily Fat Goal</label>
                  <span className="macro-slider-recommended">Recommended: {recommendedMacros?.fat}g</span>
                </div>
                <div className="macro-slider-row">
                  <input type="range" min="20" max="150" step="5"
                    value={mealSettings?.daily_fat_goal || recommendedMacros?.fat || 65}
                    onChange={(e) => setMealSettings(prev => ({ ...prev, daily_fat_goal: parseInt(e.target.value) }))}
                  />
                  <span className="macro-slider-value">
                    {mealSettings?.daily_fat_goal || recommendedMacros?.fat || 65}g
                  </span>
                </div>
              </div>
            </>
          )}

          {/* Save Button */}
          <button
            className="meal-settings-save-btn"
            onClick={saveMealSettings}
            disabled={mealSettingsLoading}
          >
            {mealSettingsLoading ? 'Saving...' : 'Save Meal Settings'}
          </button>
        </div>
      </div>
      )}

      {/* Configuration Guides Tab */}
      {settingsSubTab === 'ai-assistant' && (
        <div className="sbt-section">
          <h3 className="sbt-sectionTitle">Personalize Your AI Assistant</h3>
          <p className="sbt-sectionDesc">
            Choose an icon and name for your support assistant. It will use this name when greeting you and throughout your conversations.
          </p>

          {/* Icon Selection */}
          <div className="sbt-field">
            <label className="sbt-label">Choose an Icon</label>
            <div className="sbt-iconGrid">
              {botAvailableIcons.map(icon => (
                <button
                  key={icon}
                  className={`sbt-iconBtn ${botIcon === icon ? 'sbt-iconBtn--active' : ''}`}
                  onClick={() => setBotIcon(icon)}
                  title={icon}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>

          {/* Name Selection */}
          <div className="sbt-field">
            <label className="sbt-label">Choose a Name</label>
            <select
              className="sbt-select"
              value={botName}
              onChange={(e) => setBotName(e.target.value)}
            >
              {botAvailableNames.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>

          {/* Preview */}
          <div className="sbt-preview">
            <span className="sbt-previewIcon">{botIcon}</span>
            <span className="sbt-previewName">{botName}</span>
            <span className="sbt-previewHint">— this is how your assistant will appear</span>
          </div>

          {/* Save */}
          <div className="sbt-actions">
            <button
              className="btn btn-primary"
              onClick={saveBotPersonalization}
              disabled={botSaving}
            >
              {botSaving ? 'Saving...' : 'Save'}
            </button>
            {botSaved && <span className="sbt-success">Saved!</span>}
            {botError && <span className="sbt-error">{botError}</span>}
          </div>
        </div>
      )}

      {settingsSubTab === 'launcher' && (
        <LauncherEditor fetchWithAuth={fetchWithAuth} />
      )}

      {settingsSubTab === 'ai-access' && (
        <AIAccessConsent fetchWithAuth={fetchWithAuth} />
      )}

      {settingsSubTab === 'guides' && (
        <ConfigurationGuides
          fetchWithAuth={fetchWithAuth}
          API_BASE={API_BASE}
          token={token}
        />
      )}
      </div>
    </div>
  );
}

export default SettingsTab;
